// @ts-nocheck
import InputAdornment from '@mui/material/InputAdornment';
import { Box } from '@mui/material';
import { XGSelect } from 'xg-cl';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { XCircle, WarningCircle, User } from '@phosphor-icons/react';
import Editor from '../../../Editor/Editor';
import { setSelectedQuantity } from '../../../../redux/slices/booking/booking';
import {
  getBookingPlanDropdownOptions,
  getPlanTotal,
  planIsPastDeadline,
  planIsSoldOut,
  getTotalSelectedPassengers,
  planOccupancyExceedsAvailableCapacity
} from '../../../../utils/booking';
import { formatCurrency } from '../../../../utils/currencyHelper';

import * as PropTypes from 'prop-types';
import './BookingPlanCard.css';

/**
 * Functional React component for rendering a custom booking plan card.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {Object} [props.data] - The data for the booking plan card.
 * @param {string} [props.id] - The ID for the delete booking plan card component.
 * @param {...any} props.rest - Additional props to be spread on the parent element.
 *
 * @returns {JSX.Element} React element representing the booking plan card component.
 */

const BookingPlanCard = ({
  data,
  id,
  tripCapacity = null,
  allPlans = [],
  isFullCapacity = false,
  isTripDeparted = false,
  usedCapacity = 0,
  ...rest
}) => {
  const { t } = useTranslation(['bookings'], { useSuspense: false });
  const { title, selectedQuantity, tripplanid, description } = data;
  const dispatch = useDispatch();

  // Calculate total passengers selected in OTHER plans (excluding this one)
  const otherPlansTotal = getTotalSelectedPassengers(
    allPlans.filter((p) => p.tripplanid !== tripplanid)
  );

  // Check if this plan's occupancy exceeds remaining capacity (accounting for other user selections)
  const isPlanDisabledByCapacity = planOccupancyExceedsAvailableCapacity(
    data,
    tripCapacity,
    usedCapacity,
    otherPlansTotal
  );

  // Get dropdown options with capacity constraints
  const selectOptions = getBookingPlanDropdownOptions(
    data,
    tripCapacity,
    otherPlansTotal,
    usedCapacity
  );
  const planTotal = getPlanTotal(data);
  const isSoldOut = planIsSoldOut(data);
  const isPastDeadline = planIsPastDeadline(data);
  const isUnavailable =
    isSoldOut ||
    isPastDeadline ||
    isTripDeparted ||
    isFullCapacity ||
    isPlanDisabledByCapacity;

  const handleChange = (event, option) => {
    const { value } = option.props;
    dispatch(setSelectedQuantity({ tripplanid, quantity: Number(value) }));
  };

  return (
    <Box
      id={id}
      data-testid='booking-plan-card'
      {...rest}
      className={`booking-plan__card ${isUnavailable ? 'plan_unavailable' : ''}`}
    >
      <div className='booking-plan__card-content'>
        <div className='booking-plan__card-row'>
          <div className='booking-plan__card-quantity'>
            <XGSelect
              name='name'
              size='small'
              onChange={handleChange}
              value={`${selectedQuantity}`}
              menuItems={selectOptions}
              disabled={isUnavailable}
              className='booking-plan__card__quantity-select'
              sx={{
                '& .MuiSelect-select': {
                  paddingRight: '32px !important'
                },
                '& .MuiSelect-icon': {
                  right: '4px'
                }
              }}
              startAdornment={
                <InputAdornment
                  position='start'
                  style={{ marginBottom: '5px' }}
                >
                  <User size={20} fill='#475467' />
                </InputAdornment>
              }
            />
            <span className='text-sm-regular'>
              {selectedQuantity === 1
                ? t('bookings:passengerqty1')
                : t('bookings:passengerqty2')}
            </span>
          </div>
          <div className='booking-plan__card-title'>
            <span className='text-md-semibold'>{title}</span>
          </div>
          <div className='booking-plan__card-price'>
            <span className='h3-semibold'>{formatCurrency(planTotal)}</span>
          </div>
        </div>

        <div className='booking-plan__card-row'>
          <div className='booking-plan__card-badges'>
            {isTripDeparted ? (
              <span className='plan_pastdeadline_badge'>
                <WarningCircle size={16} />
                {t('bookings:tripHasDeparted', 'Trip has departed')}
              </span>
            ) : (
              isPastDeadline && (
                <span className='plan_pastdeadline_badge'>
                  <WarningCircle size={16} />
                  {t('bookings:planIsPastDeadline', 'Booking Deadline Passed')}
                </span>
              )
            )}
            {isSoldOut && (
              <span className='plan_soldout_badge'>
                <XCircle size={16} />
                {t('bookings:planIsSoldOut', 'Sold Out')}
              </span>
            )}
          </div>
        </div>

        {description && description !== '' && (
          <div className='booking-plan__card-description'>
            <Editor
              content={description}
              editable={false}
              isPreviewing={true}
              id='trip-output-booking-card-description-editor'
              className='third-output-editor--full-width'
            />
          </div>
        )}
      </div>
    </Box>
  );
};

BookingPlanCard.propTypes = {
  data: PropTypes.shape({
    description: PropTypes.string,
    depositamount: PropTypes.number,
    depositduedate: PropTypes.string,
    lastbookdate: PropTypes.string,
    maxavailable: PropTypes.number,
    occupancy: PropTypes.number,
    priceperperson: PropTypes.number,
    tripplanid: PropTypes.number,
    title: PropTypes.string,
    selectedQuantity: PropTypes.number,
    hideTotalBreakdown: PropTypes.bool
  }),
  id: PropTypes.string.isRequired,
  tripCapacity: PropTypes.number,
  allPlans: PropTypes.array,
  isFullCapacity: PropTypes.bool,
  isTripDeparted: PropTypes.bool,
  usedCapacity: PropTypes.number
};
export default BookingPlanCard;
